"use client";

import { useAppStore, currentView } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ThemeToggle } from "@/components/booking/theme-toggle";
import { LanguageSwitcher } from "@/components/booking/language-switcher";
import { isStaffRole, roleBadgeClass, roleLabel } from "@/components/booking/format";
import {
  Home,
  CalendarCheck,
  BookOpen,
  LogOut,
  UserCircle,
  LayoutDashboard,
  Eye,
  ChevronLeft,
  Shield,
  ShieldCheck,
  Receipt,
  PieChart,
  Bell,
} from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { apiPost, apiDelete, apiGet } from "@/lib/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useState } from "react";
import { useI18n } from "@/lib/i18n/provider";
import { PreviewUsersModal } from "@/components/booking/preview-users-modal";

// Level titles for the back arrow row.
const VIEW_ICON: Partial<Record<string, React.ComponentType<{ className?: string }>>> = {
  browse: Home,
  "my-reservations": CalendarCheck,
  host: LayoutDashboard,
  moderator: ShieldCheck,
  expenses: Receipt,
  dashboard: PieChart,
  notifications: Bell,
  admin: Shield,
  product: Home,
};

export function Header() {
  const {
    user,
    path,
    goHome,
    back,
    openAuth,
    setUser,
    openNewProduct,
    impersonating,
    setImpersonating,
    openArea,
  } = useAppStore();
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  const router = useRouter();
  const pathname = usePathname();
  const [accountOpen, setAccountOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  // The header is shared by the app container ("/") and /documentation:
  // leaving the docs route must also navigate back to "/".
  const navGoHome = () => {
    goHome();
    if (pathname !== "/") router.push("/");
  };

  const view = currentView(path);
  const depth = path.length; // 1 = home, 2 = area, 3 = detail
  const viewLabel = t(`nav.level.${view}`) !== `nav.level.${view}` ? t(`nav.level.${view}`) : t(`nav.${view}`);

  const logout = async () => {
    try {
      await apiPost("/api/auth/logout");
    } catch {
      /* ignore */
    }
    setUser(null);
    setImpersonating(false);
    qc.clear();
    navGoHome();
    toast.success(t("toast.signedOut"));
  };

  const exitPreview = async () => {
    try {
      await apiDelete("/api/auth/impersonate");
    } catch {
      /* ignore */
    }
    qc.clear();
    try {
      const res = await apiGet<{ user: any; impersonating?: boolean }>("/api/auth/me");
      setUser(res.user ?? null);
      setImpersonating(!!res.impersonating);
    } catch {
      setUser(null);
      setImpersonating(false);
    }
    navGoHome();
    toast.success(t("preview.exited"));
  };

  const displayName = user
    ? [user.firstName, user.lastName].filter(Boolean).join(" ") || user.username
    : "";

  const Icon = VIEW_ICON[view];

  return (
    <>
      <header className="sticky top-0 z-40 w-full border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        {impersonating && user && (
          <div className="border-b border-amber-200 bg-amber-100 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950 dark:text-amber-200">
            <div className="mx-auto flex h-9 max-w-7xl items-center justify-between gap-3 px-4 sm:px-6">
              <div className="flex min-w-0 items-center gap-2 text-sm">
                <Eye className="h-4 w-4 shrink-0" />
                <span className="truncate">
                  {t("preview.banner", {
                    name: displayName || user.username,
                    role: roleLabel(user.role, locale),
                  })}
                </span>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="h-7 shrink-0 gap-1.5 border-amber-300 bg-transparent px-2.5 hover:bg-amber-200/60 dark:border-amber-800 dark:hover:bg-amber-900/40"
                onClick={exitPreview}
              >
                <LogOut className="h-3.5 w-3.5" /> {t("preview.exit")}
              </Button>
            </div>
          </div>
        )}

        {/* Main bar: logo + back arrow + current level */}
        <div className="mx-auto flex h-16 max-w-7xl items-center gap-3 px-4 sm:px-6">
          {depth > 1 ? (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={back}
              aria-label={t("nav.back")}
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="hidden sm:inline">{t("nav.back")}</span>
            </Button>
          ) : (
            <Button variant="ghost" size="sm" className="gap-1.5" onClick={navGoHome} aria-label={t("nav.home")}>
              <ChevronLeft className="h-4 w-4 opacity-30" />
              <span className="hidden sm:inline opacity-50">{t("nav.home")}</span>
            </Button>
          )}

          <button onClick={navGoHome} className="flex items-center gap-2 font-semibold tracking-tight">
            <span className="grid h-9 w-9 place-items-center rounded-lg bg-primary text-primary-foreground">
              <Home className="h-5 w-5" />
            </span>
            <span className="text-lg hidden sm:inline">{t("app.name")}</span>
          </button>

          {depth > 1 && (
            <div className="ml-2 flex min-w-0 items-center gap-2 border-l pl-3">
              {Icon && <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />}
              <span className="truncate text-sm font-medium">{viewLabel}</span>
              {path.length > 2 && (
                <ChevronLeft className="h-3.5 w-3.5 rotate-180 text-muted-foreground" />
              )}
            </div>
          )}

          <div className="ml-auto flex items-center gap-1 sm:gap-2">
            {user && (user.role === "OWNER" || user.role === "ADMIN") && view === "browse" && (
              <Button size="sm" onClick={openNewProduct} className="hidden lg:inline-flex">
                {t("nav.listProperty")}
              </Button>
            )}
            <LanguageSwitcher />
            <ThemeToggle />

            {user ? (
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                aria-expanded={accountOpen}
                onClick={() => setAccountOpen((v) => !v)}
              >
                <UserCircle className="h-4 w-4" />
                <span className="hidden sm:inline max-w-[120px] truncate">{displayName}</span>
              </Button>
            ) : (
              <>
                <Button variant="ghost" size="sm" onClick={() => openAuth("login")} className="hidden sm:inline-flex">
                  {t("common.signin")}
                </Button>
                <Button size="sm" onClick={() => openAuth("register")}>
                  {t("common.signup")}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Inline account panel (no dropdown, no overlay) */}
        {accountOpen && user && (
          <div className="border-t bg-background">
            <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-3 sm:px-6">
              <div className="flex flex-wrap items-center gap-2">
                <UserCircle className="h-5 w-5 text-muted-foreground" />
                <span className="font-medium">{displayName}</span>
                <span className="text-xs text-muted-foreground">@{user.username}</span>
                <Badge variant="outline" className={`w-fit ${roleBadgeClass(user.role)}`}>
                  {roleLabel(user.role, locale)}
                </Badge>
                {user.userGroup && (
                  <Badge
                    variant="outline"
                    className={
                      user.userGroup === "FAMILY"
                        ? "w-fit border-emerald-200 bg-emerald-50 text-emerald-700"
                        : "w-fit border-sky-200 bg-sky-50 text-sky-700"
                    }
                  >
                    {t(`group.${user.userGroup}`)}
                  </Badge>
                )}
                {!user.validated && user.role === "USER" && (
                  <Badge variant="outline" className="w-fit border-amber-300 bg-amber-50 text-amber-700">
                    {t("mod.notValidated")}
                  </Badge>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-2"
                  onClick={() => {
                    openArea("my-reservations");
                    setAccountOpen(false);
                  }}
                >
                  <CalendarCheck className="h-4 w-4" /> {t("nav.myTripsShort")}
                </Button>
                <Button variant="ghost" size="sm" className="gap-2" asChild>
                  <Link href="/documentation" onClick={() => setAccountOpen(false)}>
                    <BookOpen className="h-4 w-4" /> {t("nav.documentation")}
                  </Link>
                </Button>
                {(user.role === "OWNER" || user.role === "ADMIN") && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="gap-2"
                    onClick={() => {
                      openArea("host");
                      setAccountOpen(false);
                    }}
                  >
                    <LayoutDashboard className="h-4 w-4" /> {t("nav.hostPanelShort")}
                  </Button>
                )}
                {isStaffRole(user.role) && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-2"
                      onClick={() => {
                        openArea("moderator");
                        setAccountOpen(false);
                      }}
                    >
                      <ShieldCheck className="h-4 w-4" /> {t("nav.moderator")}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-2"
                      onClick={() => {
                        openArea("expenses");
                        setAccountOpen(false);
                      }}
                    >
                      <Receipt className="h-4 w-4" /> {t("nav.expenses")}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-2"
                      onClick={() => {
                        openArea("dashboard");
                        setAccountOpen(false);
                      }}
                    >
                      <PieChart className="h-4 w-4" /> {t("nav.dashboard")}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-2"
                      onClick={() => {
                        openArea("notifications");
                        setAccountOpen(false);
                      }}
                    >
                      <Bell className="h-4 w-4" /> {t("nav.notifications")}
                    </Button>
                  </>
                )}
                {user.role === "ADMIN" && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-2"
                      onClick={() => {
                        openArea("admin");
                        setAccountOpen(false);
                      }}
                    >
                      <Shield className="h-4 w-4" /> {t("nav.adminShort")}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-2"
                      onClick={() => {
                        setPreviewOpen(true);
                        setAccountOpen(false);
                      }}
                    >
                      <Eye className="h-4 w-4" /> {t("preview.as")}
                    </Button>
                  </>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-2 text-destructive hover:text-destructive"
                  onClick={() => {
                    setAccountOpen(false);
                    logout();
                  }}
                >
                  <LogOut className="h-4 w-4" /> {t("common.signout")}
                </Button>
              </div>
            </div>
          </div>
        )}
      </header>
      <PreviewUsersModal open={previewOpen} onClose={() => setPreviewOpen(false)} />
    </>
  );
}
